import { useEffect, useState, useCallback } from "react";
import {
    Container,
    Card,
    Button,
    Spinner,
    Table,
    Modal,
    Form,
    Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getAuthToken } from "../../utils/auth";
import AdminLayout from "./AdminLayout";

const UserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [alert, setAlert] = useState(null);

    const fetchUser = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${baseUrl}/user/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const result = await res.json();
            if (result.status) setUser(result.data);
            else setAlert({ type: "danger", msg: result.message });
        } catch (err) {
            console.error("Error fetching user details:", err);
            setAlert({ type: "danger", msg: "Failed to fetch user." });
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, id]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const toggleUserStatus = async () => {
        if (!user) return;
        const confirm = window.confirm(
            `Are you sure you want to ${user.enabled ? "deactivate" : "activate"} this user?`
        );
        if (!confirm) return;

        try {
            setLoading(true);
            const res = await fetch(`${baseUrl}/user/${id}/status`, {
                method: "PATCH",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ enabled: !user.enabled }),
            });
            const result = await res.json();
            if (result.status) {
                setAlert({ type: "success", msg: "User status updated successfully!" });
                fetchUser();
            } else setAlert({ type: "danger", msg: result.message });
        } catch (err) {
            console.error(err);
            setAlert({ type: "danger", msg: "Failed to update status." });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEdit = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${baseUrl}/user/${id}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(editData),
            });
            const result = await res.json();
            if (result.status) {
                setAlert({ type: "success", msg: "User updated successfully!" });
                setShowEditModal(false);
                fetchUser();
            } else setAlert({ type: "danger", msg: result.message });
        } catch (err) {
            console.error(err);
            setAlert({ type: "danger", msg: "Failed to update user." });
        } finally {
            setLoading(false);
        }
    };

    if (loading)
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
            </div>
        );

    if (!user)
        return (
            <AdminLayout>
                <Container className="text-center mt-5">
                    <p className="text-muted">User not found.</p>
                    <Button variant="secondary" onClick={() => navigate(-1)}>Go Back</Button>
                </Container>
            </AdminLayout>
        );

    const isDeleted = Boolean(user.deletedAt);

    // Prepare key-value pairs for table
    const details = [
        ...(isDeleted
            ? [
                ["Deleted", new Date(user.deletedAt).toLocaleString()],
                ["Phone number (before deletion)", user.deletedPhoneNumber || "-"],
            ]
            : []),
        ["Full Name", user.fullName],
        ["Email", user.email],
        ["Username", user.username],
        ["Admin", user.admin ? "Yes" : "No"],
        ["Roles", user.roles?.map(r => r.name).join(", ")],
        ["Enabled", user.enabled ? "Yes" : "No"],
        ["Account Non Expired", user.accountNonExpired ? "Yes" : "No"],
        ["Credentials Non Expired", user.credentialsNonExpired ? "Yes" : "No"],
        ["Account Non Locked", user.accountNonLocked ? "Yes" : "No"],
        ["Created", new Date(user.createdDate).toLocaleString()],
        ["Last Modified", user.lastModifiedDate ? new Date(user.lastModifiedDate).toLocaleString() : "-"],
    ];

    return (
        <AdminLayout>
            <Container className="mt-4">


                {alert && (
                    <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="mt-3">
                        {alert.msg}
                    </Alert>
                )}

                {isDeleted && (
                    <Alert variant="danger" className="mt-3">
                        This account was deleted by the user on{" "}
                        <strong>{new Date(user.deletedAt).toLocaleString()}</strong>. Personal data was
                        removed; subscriptions and payments are kept for records.
                    </Alert>
                )}

                <Card className="mt-3 shadow-sm p-3">
                    <Card.Title className="mb-3 d-flex justify-content-between align-items-center">
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>← Back</Button>
                        <span>  {user.email}</span>
                        <div className={isDeleted ? "d-none" : ""}>
                            <Button
                                variant={user.enabled ? "warning" : "success"}
                                size="sm"
                                className="me-2"
                                onClick={toggleUserStatus}
                            >
                                {user.enabled ? "Deactivate" : "Activate"}
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    setEditData({
                                        firstname: user.firstname,
                                        lastname: user.lastname,
                                        roles: user.roles.map(r => r.name),
                                    });
                                    setShowEditModal(true);
                                }}
                            >
                                Edit
                            </Button>
                        </div>
                    </Card.Title>

                    {/* ✅ Zebra table for details */}
                    <Table striped bordered hover>
                        <tbody>
                            {details.map(([key, value], idx) => (
                                <tr key={idx}>
                                    <td className="text-end fw-bold">{key}</td>
                                    <td className="text-start">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>

                {/* Edit Modal */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit User</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editData.firstname || ""}
                                    onChange={(e) => setEditData({ ...editData, firstname: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editData.lastname || ""}
                                    onChange={(e) => setEditData({ ...editData, lastname: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Roles (comma separated)</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editData.roles?.join(", ") || ""}
                                    onChange={(e) =>
                                        setEditData({ ...editData, roles: e.target.value.split(",").map(r => r.trim()) })
                                    }
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleSaveEdit}>Save Changes</Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </AdminLayout>
    );
};

export default UserDetails;
